import { normalizeMediaFilePath } from '../../../../services/uiPreferenceApi';

/**
 * 計算 subGroupKey（與 MediaGallery 一致）
 * @param {string} groupKey
 * @param {string} status
 * @returns {string}
 */
export const makeSubGroupKey = (groupKey, status) => `${groupKey}__${status}`;

/**
 * 按選取清單過濾並依選取順序重排 groupedMedia（用於 PDF/Docx/ZIP 匯出）。
 * - selectedByGroup: { [subGroupKey]: string[] }（normalized paths，陣列順序即列印順序）
 * - 若某 subGroup 沒有任何選取 → 該 subGroup 排除在匯出之外
 * - 若 selectedByGroup 為空物件 → 全部不匯出（回傳空陣列）
 * @param {Array} groupedMedia
 * @param {Object} selectedByGroup
 * @returns {Array}
 */
export function filterGroupedMediaBySelection(groupedMedia, selectedByGroup) {
  if (!groupedMedia?.length) return groupedMedia;
  if (!selectedByGroup || Object.keys(selectedByGroup).length === 0) return [];

  return groupedMedia
    .map(group => ({
      ...group,
      subGroups: (group.subGroups || [])
        .map(subGroup => {
          const key = makeSubGroupKey(group.groupKey, subGroup.status);
          const selectedPaths = selectedByGroup[key];
          if (!selectedPaths || selectedPaths.length === 0) {
            return { ...subGroup, mediaFiles: [] };
          }
          const pathToFile = {};
          (subGroup.mediaFiles || []).forEach(f => {
            const np = normalizeMediaFilePath(f.filePath);
            if (np) pathToFile[np] = f;
          });
          const orderedFiles = selectedPaths.map(p => pathToFile[p]).filter(Boolean);
          return { ...subGroup, mediaFiles: orderedFiles };
        })
        .filter(sg => sg.mediaFiles.length > 0),
    }))
    .filter(g => (g.subGroups || []).length > 0);
}

/**
 * 判斷媒體檔是否已被剔除。
 * @param {object} file
 * @param {Set<string>} excludedFilePaths
 */
export function isMediaFileExcluded(file, excludedFilePaths) {
  if (!excludedFilePaths?.size || !file?.filePath) return false;
  return excludedFilePaths.has(normalizeMediaFilePath(file.filePath));
}

/**
 * 過濾 groupedMedia，移除已剔除的 mediaFiles（用於 PDF 等不顯示剔除項的場景）。
 * @param {Array} groupedMedia
 * @param {Set<string>} excludedFilePaths
 * @returns {Array}
 */
export function filterGroupedMediaExcluded(groupedMedia, excludedFilePaths) {
  if (!excludedFilePaths?.size || !groupedMedia?.length) {
    return groupedMedia;
  }

  return groupedMedia
    .map((group) => ({
      ...group,
      subGroups: (group.subGroups || [])
        .map((subGroup) => ({
          ...subGroup,
          mediaFiles: (subGroup.mediaFiles || []).filter(
            (file) => !isMediaFileExcluded(file, excludedFilePaths)
          ),
        }))
        .filter((subGroup) => subGroup.mediaFiles.length > 0),
    }))
    .filter((group) => (group.subGroups || []).length > 0);
}

/**
 * 計算可見（未剔除）圖片總數。
 */
export function countVisibleImages(groupedMedia, excludedFilePaths) {
  if (!groupedMedia?.length) return 0;
  return groupedMedia.reduce((sum, group) => {
    const groupCount = (group.subGroups || []).reduce((subSum, subGroup) => {
      const files = subGroup.mediaFiles || [];
      if (!excludedFilePaths?.size) return subSum + files.length;
      return (
        subSum + files.filter((file) => !isMediaFileExcluded(file, excludedFilePaths)).length
      );
    }, 0);
    return sum + groupCount;
  }, 0);
}
